import { z } from "zod"
import { runPreparationSummarySchema } from "./run-preparation"
import { marketplaceIdSchema } from "../marketplace-id"
import { listingStatusSchema, runOriginSchema } from "./primitives"

export const runReadinessStatusSchema = z.enum(["ready", "blocked", "unknown"])
export type RunReadinessStatus = z.infer<typeof runReadinessStatusSchema>

export const runPipelinePhaseSchema = z.enum([
  "preparing",
  "built",
  "approved",
  "importing",
  "import_recovery",
  "terminal",
])
export type RunPipelinePhase = z.infer<typeof runPipelinePhaseSchema>

export const runObservationFreshnessSchema = z.enum(["current", "stale", "unknown"])
export type RunObservationFreshness = z.infer<typeof runObservationFreshnessSchema>

// Artifact-only commitment. Source freshness hashes remain independent of mapping evidence.
const familyMappingSha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/)
  .optional()

export const runInputIdentitySchema = z.discriminatedUnion("version", [
  z.object({
    version: z.literal(1),
    hash: z.string(),
    sourceIdentity: z.string(),
    selectionFingerprint: z.string(),
    familyMappingSha256: familyMappingSha256Schema,
    familySourceSha256: familyMappingSha256Schema,
  }),
  z.object({
    version: z.literal(2),
    hash: z.string(),
    /** Feed-build identity retained inside validation identities. This binds a
     * validation and its coverage to the exact artifact inputs without making
     * later catalog-mirror changes rewrite terminal history. */
    feedHash: z.string(),
    sourceIdentity: z.string(),
    selectionFingerprint: z.string(),
    familyMappingSha256: familyMappingSha256Schema,
    familySourceSha256: familyMappingSha256Schema,
  }),
  z.object({
    version: z.literal(3),
    hash: z.string(),
    /** Feed-build identity retained inside validation identities. This binds a
     * validation and its coverage to the exact artifact inputs without making
     * later catalog-mirror changes rewrite terminal history. */
    feedHash: z.string(),
    sourceIdentity: z.string(),
    selectionFingerprint: z.string(),
    familyMappingSha256: familyMappingSha256Schema,
    familySourceSha256: familyMappingSha256Schema,
  }),
])
export type RunInputIdentity = z.infer<typeof runInputIdentitySchema>

export const runStatusObservationSchema = z.object({
  source: z.string(),
  observedAt: z.string().nullable(),
  freshness: runObservationFreshnessSchema,
  count: z.number().int().nonnegative().nullable(),
  sampleSkus: z.array(z.string()).max(25),
  reasonCodes: z.array(z.string()).max(25),
})
export type RunStatusObservation = z.infer<typeof runStatusObservationSchema>

export const runStatusCoverageDimensionSchema = z.object({
  filled: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percentage: z.number().min(0).max(100).nullable(),
  measuredConfigCount: z.number().int().nonnegative(),
  unmeasuredConfigCount: z.number().int().nonnegative(),
  cohortIdentity: z.string(),
  measurementReasons: z.array(
    z.object({ reason: z.string(), count: z.number().int().nonnegative() })
  ),
})
export type RunStatusCoverageDimension = z.infer<typeof runStatusCoverageDimensionSchema>

export const runStatusSummarySchema = z.object({
  identity: z.object({
    id: z.number().int(),
    slug: z.string(),
    marketplace: marketplaceIdSchema,
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  lifecycle: z.object({
    status: listingStatusSchema,
    error: z.string().nullable(),
    marketplaceStatus: z.string().nullable(),
    marketplaceImportId: z.string().nullable(),
    marketplaceStatusObservedAt: z.string().nullable(),
    marketplaceImportTerminalAt: z.string().nullable(),
  }),
  pipeline: z.object({ phase: runPipelinePhaseSchema }),
  automation: z.object({
    origin: runOriginSchema,
    sourceEventCount: z.number().int().nonnegative(),
    latestEventId: z.number().int().positive().nullable(),
    latestSourceUpdatedAt: z.string().nullable(),
    state: z.enum(["manual", "dirty", "current"]),
    preparation: runPreparationSummarySchema.nullable().optional(),
  }),
  products: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    ok: z.number().int().nonnegative(),
    fixableNok: z.number().int().nonnegative(),
    businessExcludedNok: z.number().int().nonnegative(),
    sourceBlockedNok: z.number().int().nonnegative(),
    setAside: z.number().int().nonnegative(),
  }),
  readiness: z.object({
    status: runReadinessStatusSchema,
    blockers: z.array(
      z.object({
        kind: z.string(),
        count: z.number().int().nonnegative(),
        sampleSkus: z.array(z.string()).max(25),
        reasonCodes: z.array(z.string()).max(25),
      })
    ),
  }),
  coverage: z.object({
    required: runStatusCoverageDimensionSchema,
    recommended: runStatusCoverageDimensionSchema,
    optional: runStatusCoverageDimensionSchema,
  }),
  feed: z.object({
    artifactId: z.number().int().nullable(),
    artifactIdentity: z.string().nullable(),
    builtAt: z.string().nullable(),
    rowCount: z.number().int().nullable(),
    gapCount: z.number().int().nullable(),
    approval: z.object({
      state: z.enum(["never_observed", "approved", "stale"]),
      observedAt: z.string().nullable(),
      actor: z.string().nullable(),
    }),
  }),
  import: z.object({
    id: z.string().nullable(),
    observedStatus: z.string().nullable(),
    observationTime: z.string().nullable(),
    terminalTime: z.string().nullable(),
    rejections: z.object({
      state: z.enum(["never_observed", "observed"]),
      count: z.number().int().nonnegative(),
      observedAt: z.string().nullable(),
    }),
  }),
  observations: z.array(runStatusObservationSchema),
})
export type RunStatusSummary = z.infer<typeof runStatusSummarySchema>
